/*
  TwoFactorAuthentication.tsx
  - 2FA OTP entry page for /2fa route
  - Uses Tailwind CSS, global styles, and Lucide React icons
  - Supports dark/light mode, 10-minute timer, resend logic, and navigation to login on timeout
  - Integrates with login token logic and API for OTP verification
*/

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { accountHref, isReturnablePath } from '@/features/site/accountLinks';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { CheckCircle, XCircle, ShieldCheck, Clock, AlertTriangle, Lock, Shield } from 'lucide-react';
import axios from 'axios';
import '@/global.css';
import './TwoFactorAuthentication.css';
import baseURL from "@/http";
import {
    ChooseContextDialog,
    NoAccessDialog,
    currentSessionEmail,
    useCompleteSignIn,
} from "@/pages/public/Login/ChooseContext";
import {
    isLinkReturnTarget,
    readLinkReturn,
    rememberLinkReturn,
} from "@/pages/public/Login/linkReturn";
import { getDeviceType, getDeviceUid } from "@/lib/deviceId";
import { useBranding, useBrandLogo, useBrandName } from '@/contexts/BrandingContext';

const OTP_LENGTH = 6;
const TIMER_DURATION = 10 * 60; // 10 minutes in seconds
const DEFAULT_REDIRECT_TO = '/admin_dashboard_page';

const sanitizeRedirectTarget = (
    target: string | null | undefined,
    fallback = DEFAULT_REDIRECT_TO
) => {
    if (!target) return fallback;

    try {
        if (target.startsWith('http://') || target.startsWith('https://')) {
            const url = new URL(target);
            if (url.origin !== window.location.origin) return fallback;
            target = `${url.pathname}${url.search}${url.hash}`;
        }
    } catch {
        return fallback;
    }

    if (!target.startsWith('/') || target.startsWith('//')) return fallback;
    if (target.startsWith('/login') || target.startsWith('/signup') || target.startsWith('/2fa') || !isReturnablePath(target)) {
        return fallback;
    }
    return target;
};

const TwoFactorAuthentication: React.FC = () => {
    // Whose screen this is. Falls back to the platform defaults, so this
    // renders exactly as before on our own domain.
    const brandName = useBrandName();
    const { branding } = useBranding();
    const brandTagline = branding.tagline || '';
    const { logoUrl: brandLogo, fallbackUrl: platformLogo } = useBrandLogo();
    const [brandLogoFailed, setBrandLogoFailed] = useState(false);
    const [otp, setOtp] = useState('');
    const [timer, setTimer] = useState(TIMER_DURATION);
    const [isResendEnabled, setIsResendEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [isValidSession, setIsValidSession] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const explicitRedirect =
        new URLSearchParams(location.search).get('redirect') ||
        (location.state as { returnTo?: string } | null)?.returnTo;
    const postLoginRedirect = sanitizeRedirectTarget(
        // A link target remembered by the login page stands in when this page
        // lost its own — see linkReturn.
        explicitRedirect || readLinkReturn()
    );
    // Where the error paths below send the user. A sign-in on its way back to
    // a shareable link keeps that link, or the second attempt would be treated
    // as a direct sign-in and refused.
    const loginPath = isLinkReturnTarget(postLoginRedirect)
        ? `${accountHref('/login')}?redirect=${encodeURIComponent(postLoginRedirect)}`
        : accountHref('/login');

    useEffect(() => {
        if (explicitRedirect) rememberLinkReturn(sanitizeRedirectTarget(explicitRedirect));
    }, [explicitRedirect]);

    /**
     * For a 2FA account the session slot is claimed when the CODE is verified,
     * not when the password was accepted — so this response, not the login one,
     * is where the backend reports a device it signed out to make room. Held in
     * a ref because the toast fires from finishLogin, which may run a context
     * choice later and must not depend on state that has not flushed.
     */
    const signedOutNoteRef = useRef<string>('');

    /** Land on the requested page. The single exit for every successful path. */
    const finishLogin = useCallback((target: string) => {
        setSuccess(true);
        setFailedAttempts(0);
        toast({
            title: '2FA Success',
            description: `You are now logged in.${signedOutNoteRef.current}`
        });
        setTimeout(() => navigate(target, { replace: true }), 1500);
    }, [navigate, toast]);

    // ── Multi-institute login, on the 2FA path ────────────────────────────
    // A 2FA sign-in is still a sign-in: it owes the same institute choice, and
    // the same no-access rule — including the exception for a sign-in on its
    // way back to a shareable link. One copy of that rule, in useCompleteSignIn.
    const {
        completeSignIn,
        noAccessDialogProps,
        contextDialogProps,
    } = useCompleteSignIn({
        getReturnTarget: () => postLoginRedirect,
        onFinish: (target) => finishLogin(target),
        // "Back to sign in" means it: this page has nothing left to offer once
        // the code is spent, and staying here showed a dead OTP form.
        onNoAccessClosed: () => navigate(accountHref('/login'), { replace: true }),
    });

    /** Ask what this account may sign in as, then finish accordingly. */
    const completeLogin = useCallback(
        () => completeSignIn(currentSessionEmail(), null),
        [completeSignIn],
    );

    // Validate session on mount and calculate remaining time
    useEffect(() => {
        const userId = localStorage.getItem('2fa_user_id');
        const sessionId = localStorage.getItem('2fa_session_id');
        const timestamp = localStorage.getItem('2fa_timestamp');

        // Check if required data exists
        if (!userId || !sessionId || !timestamp) {
            setIsValidSession(false);
            setError('Invalid or expired session. Please login again.');
            toast({
                title: 'Session Expired',
                description: 'Your 2FA session has expired. Please login again.',
                variant: 'destructive',
            });

            // Clean up any partial data
            localStorage.removeItem('2fa_user_id');
            localStorage.removeItem('2fa_session_id');
            localStorage.removeItem('2fa_timestamp');

            // Redirect to login after 3 seconds
            setTimeout(() => navigate(loginPath), 3000);
            return;
        }

        // Check if 10 minutes have passed and calculate remaining time
        const now = Date.now();
        const sessionTime = parseInt(timestamp);
        const timeDiff = now - sessionTime;
        const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

        if (timeDiff > tenMinutes) {
            setIsValidSession(false);
            setError('Your 2FA session has expired (10 minutes limit). Please login again.');
            toast({
                title: 'Session Expired',
                description: 'Your 2FA session has expired. Please login again.',
                variant: 'destructive',
            });

            // Clean up expired session data
            localStorage.removeItem('2fa_user_id');
            localStorage.removeItem('2fa_session_id');
            localStorage.removeItem('2fa_timestamp');

            // Redirect to login after 3 seconds
            setTimeout(() => navigate(loginPath), 3000);
        } else {
            // Calculate remaining time in seconds
            const remainingMs = tenMinutes - timeDiff;
            const remainingSeconds = Math.floor(remainingMs / 1000);
            setTimer(remainingSeconds);
        }
    }, [navigate, toast, loginPath]);

    // Start countdown timer
    useEffect(() => {
        if (!isValidSession) return; // Don't start timer if session is invalid

        if (timer <= 0) {
            setIsResendEnabled(true);
            // After timer ends, navigate to login after 5 seconds
            timerRef.current = setTimeout(() => navigate(loginPath), 5000) as any;
            return;
        }
        timerRef.current = setTimeout(() => setTimer((t) => t - 1), 1000) as any;
        return () => timerRef.current && clearTimeout(timerRef.current);
    }, [timer, navigate, isValidSession, loginPath]);

    // Cleanup on unmount
    useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

    // Format timer as mm:ss
    const formatTime = (t: number) => {
        const m = Math.floor(t / 60);
        const s = t % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Handle OTP input
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH);
        setOtp(value);
        setError('');
    };

    // Handle OTP submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            // Get user_id and session_id from localStorage (should be set during login)
            const userId = localStorage.getItem('2fa_user_id');
            const sessionId = localStorage.getItem('2fa_session_id');

            if (!userId || !sessionId) {
                setError('Session expired. Please login again.');
                setTimeout(() => navigate(loginPath), 2000);
                return;
            }

            const res = await axios.post(`${baseURL}/public/2fa/`,
                {
                    user_id: userId,
                    code: otp,
                    session_id: sessionId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // The slot is claimed when the code is verified, so
                        // this request must identify the client the same way
                        // the sign-in did.
                        'X-Device-Type': getDeviceType(),
                        'X-Device-Id': getDeviceUid(),
                    }
                }
            );

            // On success, update tokens and navigate to dashboard similar to login
            if (res.status === 200 && res.data) {
                // Store tokens as per login page logic
                if (res.data.access) {
                    localStorage.setItem('access_token', res.data.access);
                }
                if (res.data.refresh) {
                    localStorage.setItem('refresh_token', res.data.refresh);
                }
                if (res.data.expires_in) {
                    const currentTime = Date.now();
                    const bufferTime = 2 * 60 * 1000; // 2 minute buffer
                    const effectiveExpiration = currentTime + res.data.expires_in * 1000 - bufferTime;
                    localStorage.setItem('token_expiration', effectiveExpiration.toString());
                }
                if (res.data.session_id) {
                    localStorage.setItem('session_id', res.data.session_id);
                }

                const signedOut = res.data.signed_out_device?.message;
                signedOutNoteRef.current =
                    typeof signedOut === 'string' && signedOut ? ` ${signedOut}.` : '';

                // Clear 2FA temporary data
                localStorage.removeItem('2fa_user_id');
                localStorage.removeItem('2fa_session_id');
                localStorage.removeItem('2fa_timestamp');

                // The OTP is verified and the tokens are stored, but the
                // session is not open yet: it may still owe an institute
                // choice. completeLogin navigates once that is settled.
                await completeLogin();
            } else {
                setError('Invalid response from server.');
                setFailedAttempts(prev => prev + 1);
            }
        } catch (err: any) {
            const errorMsg = err?.response?.data?.error || err?.response?.data?.message || 'Invalid OTP. Please try again.';
            setError(errorMsg);
            setOtp('');

            // Increment failed attempts
            const newFailedAttempts = failedAttempts + 1;
            setFailedAttempts(newFailedAttempts);

            // Enable resend after 2 failed attempts
            if (newFailedAttempts >= 2) {
                setIsResendEnabled(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Handle resend OTP
    const handleResend = async () => {
        setIsResendEnabled(false);
        setTimer(TIMER_DURATION);
        setError('');
        setIsLoading(true);

        try {
            // Get user_id and session_id from localStorage
            const userId = localStorage.getItem('2fa_user_id');
            const sessionId = localStorage.getItem('2fa_session_id');

            if (!userId || !sessionId) {
                setError('Session expired. Please login again.');
                setTimeout(() => navigate(loginPath), 2000);
                return;
            }

            const res = await axios.post(`${baseURL}/public/2faResendMail/`,
                {
                    user_id: userId,
                    session_id: sessionId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // The slot is claimed when the code is verified, so
                        // this request must identify the client the same way
                        // the sign-in did.
                        'X-Device-Type': getDeviceType(),
                        'X-Device-Id': getDeviceUid(),
                    }
                }
            );

            // Check for success response
            if (res.status === 200 && res.data?.message) {
                // Update timestamp in localStorage to reset 10-minute window
                localStorage.setItem('2fa_timestamp', Date.now().toString());
                
                toast({
                    title: 'OTP Sent',
                    description: res.data.message || 'A new OTP has been sent to your device.'
                });
                setFailedAttempts(0); // Reset failed attempts after successful resend
                setError(''); // Clear any existing errors
            }
        } catch (err: any) {
            const errorMsg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to resend OTP. Please try again.';
            setError(errorMsg);
            setIsResendEnabled(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="twofa-container">
            <div className="twofa-content">
                {/* Header with Logo */}
                <div className="twofa-header">
                    <div className="twofa-logo-section">
                        <div className="twofa-logo">
                            <div className="twofa-logo-icon">
                                <img
                  src={brandLogoFailed ? platformLogo : brandLogo}
                  alt={brandName}
                  onError={() => setBrandLogoFailed(true)}
                />
                            </div>
                            <h1 className="twofa-logo-text">{brandName}</h1>
                        </div>
                    {brandTagline ? (
                      <p className="text-sm text-muted-foreground mt-1">{brandTagline}</p>
                    ) : null}
                    </div>
                </div>

                <Card className="twofa-card">
                    <CardHeader className="twofa-card-header">
                        {/* Icon Section */}
                        <div className="twofa-icon-wrapper">
                            <div className="twofa-icon-bg">
                                <ShieldCheck className="w-12 h-12 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                            </div>
                        </div>
                        <CardTitle className="twofa-card-title">Two-Factor Authentication</CardTitle>
                        <CardDescription>
                            Enter the 6-digit code sent to your device.
                        </CardDescription>
                        {/* Timer Display */}
                        <div className="twofa-timer">
                            <Clock className="w-4 h-4" />
                            <span className="twofa-timer-text">
                                Expires in <span className="twofa-timer-value">{formatTime(timer)}</span>
                            </span>
                        </div>
                    </CardHeader>

                    <CardContent className="twofa-card-content">
                        {/* Error Alert */}
                        {error && (
                            <Alert variant="destructive" className="twofa-error">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        {/* Success Message */}
                        {success && (
                            <div className="twofa-success">
                                <CheckCircle className="w-5 h-5" />
                                Success! Redirecting to dashboard...
                            </div>
                        )}

                        {!isValidSession ? (
                            <div className="twofa-invalid-session">
                                <p className="text-center text-gray-600 dark:text-gray-400">
                                    Redirecting to login page...
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="twofa-form">
                                {/* OTP Input */}
                                <div className="twofa-input-wrapper">
                                    <Input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={OTP_LENGTH}
                                        value={otp}
                                        onChange={handleChange}
                                        disabled={isLoading || success || timer <= 0}
                                        className="twofa-input"
                                        placeholder="● ● ● ● ● ●"
                                        autoFocus
                                        required
                                    />
                                </div>

                                {/* Verify Button */}
                                <Button
                                    type="submit"
                                    disabled={otp.length !== OTP_LENGTH || isLoading || success || timer <= 0}
                                    className="twofa-submit-btn"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="twofa-spinner" />
                                            Verifying...
                                        </>
                                    ) : (
                                        'Verify Code'
                                    )}
                                </Button>

                                {/* Resend Button */}
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleResend}
                                    disabled={!isResendEnabled || isLoading}
                                    className="twofa-resend-btn"
                                >
                                    Resend Code
                                </Button>

                                {/* Expired Notice */}
                                {timer <= 0 && (
                                    <p className="twofa-expired-text">
                                        Code expired. You will be redirected to login.
                                    </p>
                                )}
                            </form>
                        )}
                    </CardContent>
                </Card>

                {/* Trust Indicators */}
                <div className="twofa-trust-indicators">
                    <div className="twofa-trust-item">
                        <Shield className="twofa-trust-icon" />
                        <span>SOC 2 Secure</span>
                    </div>
                    <div className="twofa-trust-item">
                        <Lock className="twofa-trust-icon" />
                        <span>256-bit SSL</span>
                    </div>
                    <div className="twofa-trust-item">
                        <CheckCircle className="twofa-trust-icon" />
                        <span>FERPA Compliant</span>
                    </div>
                </div>
            </div>

            {/* The same two dialogs Login.tsx shows. A 2FA sign-in is still a
                sign-in: it owes the same institute choice, and an account linked
                to nothing must be told so rather than routed into an empty app. */}
            <NoAccessDialog {...noAccessDialogProps} />

            <ChooseContextDialog {...contextDialogProps} />
        </div>
    );
};

export default TwoFactorAuthentication;
